import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import {
  Box, 
  Typography, 
  Paper, 
  Avatar, 
  TextField,
  Button, 
  Alert, 
  CircularProgress,
  Divider,
  LinearProgress,
  Container,
  Fade,
  Grid,
  useMediaQuery,
  InputAdornment,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  useTheme
} from "@mui/material";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// Password strength calculation function
const calculatePasswordStrength = (password) => {
  if (!password) return 0;
  
  // Calculate password strength based on criteria
  let strength = 0;
  
  // Length check
  if (password.length >= 8) strength += 25;
  
  // Contains lowercase letter
  if (/[a-z]/.test(password)) strength += 25;
  
  // Contains uppercase letter
  if (/[A-Z]/.test(password)) strength += 25;
  
  // Contains number or special character
  if (/[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password)) strength += 25;
  
  return Math.min(100, strength);
};

// Get color based on password strength
const getStrengthColor = (strength) => {
  if (strength < 25) return '#f44336'; // Red
  if (strength < 50) return '#ff9800'; // Orange
  if (strength < 75) return '#ffeb3b'; // Yellow
  return '#4caf50'; // Green
};

// Get strength label
const getStrengthLabel = (strength) => {
  if (strength < 25) return 'Weak';
  if (strength < 50) return 'Fair';
  if (strength < 75) return 'Good';
  return 'Strong';
};

// Styled components for enhanced visuals
const GradientPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
  overflow: 'hidden',
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  position: 'relative',
}));

const GradientDivider = styled(Box)(({ theme }) => ({
  height: 8,
  width: '100%',
  background: 'linear-gradient(90deg, #0062cc 0%, #0073e6 50%, #2196f3 100%)',
  backgroundSize: '200% auto',
  animation: 'gradient 8s linear infinite',
  '@keyframes gradient': {
    '0%': { backgroundPosition: '0% center' },
    '100%': { backgroundPosition: '200% center' }
  }
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  backgroundColor: alpha(theme.palette.background.paper, 0.7),
  backdropFilter: 'blur(10px)',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 20px ${alpha(theme.palette.primary.main, 0.15)}`
  }
}));

const AnimatedAvatar = styled(Avatar)(({ theme }) => ({
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: `0 6px 12px ${alpha(theme.palette.primary.main, 0.2)}`
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  padding: '12px 24px',
  textTransform: 'none',
  fontWeight: 600,
  position: 'relative',
  overflow: 'hidden',
  background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
  backgroundSize: '200% auto',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundPosition: 'right center',
    boxShadow: `0 6px 15px ${alpha(theme.palette.primary.main, 0.4)}`
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    animation: 'shine 1.5s infinite',
  },
  '@keyframes shine': {
    '100%': { left: '200%' }
  }
}));

const RegisterPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const { register: registerUser } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: ''
    }
  });
  
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  // Watch for password changes to update strength indicator
  const password = watch('password');
  
  useEffect(() => {
    if (password) {
      setPasswordStrength(calculatePasswordStrength(password));
    } else {
      setPasswordStrength(0);
    }
  }, [password]);

  // Add these state variables for visual enhancements
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Background image rotation for visual interest
  const backgroundImages = [
    '/images/enterprise-bg-1.jpg',
    '/images/enterprise-bg-2.jpg',
    '/images/enterprise-bg-3.jpg'
  ];
  
  // Rotate background images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);
  
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      
      console.log('Form data submitted:', { 
        ...data, 
        password: '[REDACTED]', 
        confirmPassword: '[REDACTED]' 
      });
      
      // Check if tenant context exists
      if (!tenant || !tenant.id) {
        setError('You must be invited to join an organization. Please use the invitation link provided or contact your organization administrator.');
        setIsLoading(false);
        return;
      }
      
      // Create the registration payload with field names matching the User model
      const registrationData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        tenantId: tenant._id || tenant.id,
        status: 'pending' // Set initial status as pending
      };
      
      console.log('Registering with existing tenant:', tenant.name);
      console.log('Using tenant ID:', tenant._id || tenant.id);
      
      // Call the auth context registerUser method
      const response = await registerUser(registrationData);
      console.log('Registration successful with tenant:', response);
      
      setSuccess(true);
      
      // Redirect to pending approval page after successful registration
      setTimeout(() => {
        navigate('/pending-approval', { 
          state: { 
            email: data.email,
            tenantName: tenant.name,
            userName: `${data.firstName} ${data.lastName}`
          } 
        });
      }, 3000);
      
    } catch (error) {
      console.error('Registration error details:', error);
      
      // Enhanced error handling
      if (error.response) {
        // Get the most descriptive error message
        const errorMessage = 
          error.response.data?.message || 
          error.response.data?.error ||
          `Error ${error.response.status}: ${error.response.statusText || 'Registration failed'}`;
        
        // Special handling for common errors
        if (errorMessage.includes('Tenant ID is required')) {
          setError('You need to be invited to join an organization. Please use the invitation link provided or contact your organization administrator.');
        } else if (errorMessage.includes('already exists')) {
          setError('A user with this email already exists. Please login instead or use a different email address.');
        } else {
          setError(errorMessage);
        }
        
        console.error('Server error response:', error.response.data);
      } else if (error.request) {
        setError('No response from server. Please check your internet connection and try again.');
        console.error('No response received:', error.request);
      } else {
        setError(error.message || 'An error occurred during registration. Please try again.');
        console.error('Request setup error:', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Enhanced enterprise features with better presentation
  const enterpriseFeatures = [
    {
      icon: <SecurityOutlinedIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: "Enterprise Security",
      text: "Advanced data protection with industry-standard encryption and security practices"
    },
    {
      icon: <BusinessCenterOutlinedIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: "Business Tools",
      text: "Comprehensive suite of enterprise management and collaboration tools"
    },
    {
      icon: <VerifiedUserIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: "Compliance Ready",
      text: "Built to help meet regulatory requirements with detailed audit logs"
    }
  ];
  
  if (success) {
    return (
      <Box 
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, rgba(10, 20, 41, 0.92), rgba(26, 42, 77, 0.95))',
          backgroundImage: `url(${backgroundImages[currentImageIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay',
          padding: { xs: 2, sm: 4 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-image 2s ease-in-out',
        }}
      >
        <Fade in={true} timeout={800}>
          <Paper 
            elevation={24}
            sx={{
              padding: { xs: 3, sm: 4 },
              borderRadius: 4,
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              maxWidth: '500px',
              width: '100%',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Animated success accent border */}
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '8px',
                background: 'linear-gradient(90deg, #43a047 0%, #66bb6a 50%, #81c784 100%)',
                backgroundSize: '200% auto',
                animation: 'gradient 3s linear infinite',
                '@keyframes gradient': {
                  '0%': { backgroundPosition: '0% center' },
                  '100%': { backgroundPosition: '200% center' }
                }
              }} 
            />
            
            {/* Success glow effect */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '250px',
                height: '250px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(76, 175, 80, 0.15) 0%, rgba(255, 255, 255, 0) 70%)',
                zIndex: 0
              }}
            />
            
            <AnimatedAvatar
              sx={{
                bgcolor: '#4caf50',
                width: 72,
                height: 72,
                margin: '0 auto 24px',
                boxShadow: '0 4px 16px rgba(76, 175, 80, 0.4)',
                position: 'relative',
                zIndex: 1
              }}
            >
              <CheckCircleIcon fontSize="large" />
            </AnimatedAvatar>
            
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: '#2c3e50', 
                mb: 2,
                position: 'relative',
                zIndex: 1
              }}
            >
              Registration Successful!
            </Typography>
            
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                mb: 4,
                position: 'relative',
                zIndex: 1,
                fontSize: '1.1rem'
              }}
            >
              Your account is now pending approval from the administrator of {tenant?.name || "our platform"}.
            </Typography>
            
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 4,
                position: 'relative',
                zIndex: 1,
                fontSize: '0.9rem',
                fontStyle: 'italic'
              }}
            >
              Redirecting to status page...
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          </Paper>
        </Fade>
      </Box>
    );
  }
  
  return (
    <Box 
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, rgba(10, 20, 41, 0.92), rgba(26, 42, 77, 0.95))',
        backgroundImage: `url(${backgroundImages[currentImageIndex]})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'overlay',
        padding: { xs: 2, sm: 4 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-image 2s ease-in-out',
      }}
    >
      {/* Brand Logo/Header */}
      <Box sx={{ 
        position: "absolute", 
        top: 0, 
        left: 0, 
        width: "100%", 
        py: 3, 
        px: 4, 
        textAlign: "center",
        zIndex: 2
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          color="white" 
          fontWeight="700"
          sx={{ 
            textShadow: '0 2px 10px rgba(0,0,0,0.3)',
            letterSpacing: '0.5px'
          }}
        >
          {tenant?.name || "Enterprise Portal"}
        </Typography>
      </Box>

      <Container 
        maxWidth={isTablet ? "sm" : "lg"} 
        sx={{ 
          position: "relative",
          zIndex: 1,
          my: 4,
          px: { xs: 2, sm: 3 }
        }}
      >
        <Grid container spacing={4} alignItems="center">
          {/* Left side - registration form */}
          <Grid item xs={12} md={7}>
            <Fade in={true} timeout={800}>
              <GradientPaper elevation={24}>
                <GradientDivider />
                
                <Box 
                  sx={{ 
                    pt: 5,
                    pb: 3,
                    px: { xs: 3, sm: 5 },
                    position: 'relative'
                  }}
                >
                  {/* Abstract decorative elements */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: 20,
                      width: '120px',
                      height: '120px',
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(25, 118, 210, 0.04) 0%, rgba(255, 255, 255, 0) 70%)',
                      zIndex: 0
                    }}
                  />
                  
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 30,
                      left: 20,
                      width: '150px',
                      height: '150px',
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, rgba(25, 118, 210, 0.04) 0%, rgba(255, 255, 255, 0) 70%)',
                      zIndex: 0
                    }}
                  />
                  
                  {/* Header section */}
                  <Box 
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      mb: 4,
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    {/* Avatar with glowing effect */}
                    <Box 
                      sx={{ 
                        borderRadius: '50%',
                        background: 'rgba(25, 118, 210, 0.08)',
                        p: 1.5,
                        mb: 2,
                        position: 'relative'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          background: 'radial-gradient(circle, rgba(25, 118, 210, 0.2) 0%, rgba(255, 255, 255, 0) 70%)',
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 0.6, transform: 'translate(-50%, -50%) scale(1)' },
                            '70%': { opacity: 0, transform: 'translate(-50%, -50%) scale(1.5)' },
                            '100%': { opacity: 0, transform: 'translate(-50%, -50%) scale(1.5)' },
                          }
                        }}
                      />
                      {tenant?.logo ? (
                        <img
                          style={{ 
                            height: '56px', 
                            width: 'auto',
                            position: 'relative'
                          }}
                          src={tenant.logo}
                          alt={tenant.name}
                        />
                      ) : (
                        <AnimatedAvatar 
                          sx={{
                            bgcolor: 'primary.main',
                            width: 56,
                            height: 56,
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)',
                            position: 'relative'
                          }}
                        >
                          <PersonAddIcon sx={{ fontSize: 32 }} />
                        </AnimatedAvatar>
                      )}
                    </Box>
                    
                    <Typography 
                      variant="h4" 
                      component="h1" 
                      fontWeight="700" 
                      sx={{ 
                        mb: 1, 
                        color: 'text.primary',
                        background: 'linear-gradient(90deg, #2c3e50, #1a365d)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Create your account
                    </Typography>
                    
                    {tenant && (
                      <Typography 
                        variant="h6" 
                        color="text.secondary" 
                        align="center" 
                        sx={{ mb: 1 }}
                      >
                        Join {tenant.name}
                      </Typography>
                    )}
                  </Box>

                  {/* Error alert */}
                  {error && (
                    <Alert 
                      severity="error" 
                      variant="filled"
                      sx={{ 
                        mb: 3, 
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(211, 47, 47, 0.2)'
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  {/* Form with clearly separated fields */}
                  <Box 
                    component="form" 
                    onSubmit={handleSubmit(onSubmit)}
                    sx={{
                      width: '100%',
                      '& .MuiTextField-root': {
                        mb: 2, 
                        position: 'relative',
                        zIndex: 1
                      }
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Controller
                          name="firstName"
                          control={control}
                          rules={{
                            required: 'First name is required',
                            minLength: {
                              value: 2,
                              message: 'First name must be at least 2 characters'
                            }
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              id="firstName"
                              label="First Name"
                              error={!!errors.firstName}
                              helperText={errors.firstName?.message || ' '}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonAddIcon color="primary" />
                                  </InputAdornment>
                                ),
                                sx: {
                                  borderRadius: 2,
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.1)'
                                  },
                                  '&.Mui-focused': {
                                    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.15)'
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Controller
                          name="lastName"
                          control={control}
                          rules={{
                            required: 'Last name is required',
                            minLength: {
                              value: 2,
                              message: 'Last name must be at least 2 characters'
                            }
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              id="lastName"
                              label="Last Name"
                              error={!!errors.lastName}
                              helperText={errors.lastName?.message || ' '}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonAddIcon color="primary" />
                                  </InputAdornment>
                                ),
                                sx: {
                                  borderRadius: 2,
                                  transition: 'box-shadow 0.3s ease',
                                  '&:hover': {
                                    boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.1)'
                                  },
                                  '&.Mui-focused': {
                                    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.15)'
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    
                    <Controller
                      name="email"
                      control={control}
                      rules={{
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          id="email"
                          label="Email Address"
                          type="email"
                          error={!!errors.email}
                          helperText={errors.email?.message || ' '}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MailOutlineIcon color="primary" />
                              </InputAdornment>
                            ),
                            sx: {
                              borderRadius: 2,
                              transition: 'box-shadow 0.3s ease',
                              '&:hover': {
                                boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.1)'
                              },
                              '&.Mui-focused': {
                                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.15)'
                              }
                            }
                          }}
                        />
                      )}
                    />
                    
                    <Controller
                      name="password"
                      control={control}
                      rules={{
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters'
                        }
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          id="password"
                          label="Password"
                          type={showPassword ? "text" : "password"}
                          error={!!errors.password}
                          helperText={errors.password?.message || ' '}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockOutlinedIcon color="primary" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                >
                                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                            sx: {
                              borderRadius: 2,
                              transition: 'box-shadow 0.3s ease',
                              '&:hover': {
                                boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.1)'
                              },
                              '&.Mui-focused': {
                                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.15)'
                              }
                            }
                          }}
                        />
                      )}
                    />
                    
                    {/* Password strength meter with enhanced visuals */}
                    {password && (
                      <Box sx={{ 
                        mt: -1,
                        mb: 2,
                        position: 'relative', 
                        zIndex: 1 
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: getStrengthColor(passwordStrength), 
                              fontWeight: 600,
                              textShadow: `0 0 6px ${getStrengthColor(passwordStrength)}20`
                            }}
                          >
                            Password strength: {getStrengthLabel(passwordStrength)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {passwordStrength}%
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            position: 'relative',
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'rgba(0,0,0,0.08)',
                            overflow: 'hidden'
                          }}
                        >
                          <LinearProgress 
                            variant="determinate" 
                            value={passwordStrength} 
                            sx={{ 
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              height: '100%',
                              borderRadius: 4,
                              backgroundColor: 'transparent',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getStrengthColor(passwordStrength),
                                boxShadow: `0 0 10px ${getStrengthColor(passwordStrength)}80`
                              }
                            }} 
                          />
                        </Box>
                        
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          Use 8+ characters with letters, numbers & special characters
                        </Typography>
                      </Box>
                    )}
                    
                    <Controller
                      name="confirmPassword"
                      control={control}
                      rules={{
                        required: 'Please confirm your password',
                        validate: value => value === password || 'Passwords do not match'
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          id="confirmPassword"
                          label="Confirm Password"
                          type={showConfirmPassword ? "text" : "password"}
                          error={!!errors.confirmPassword}
                          helperText={errors.confirmPassword?.message || ' '}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockOutlinedIcon color="primary" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle confirm password visibility"
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  edge="end"
                                >
                                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                            sx: {
                              borderRadius: 2,
                              transition: 'box-shadow 0.3s ease',
                              '&:hover': {
                                boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.1)'
                              },
                              '&.Mui-focused': {
                                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.15)'
                              }
                            }
                          }}
                        />
                      )}
                    />
                    
                    {/* Submit Button with enhanced styling */}
                    <StyledButton
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={isLoading}
                      sx={{
                        mt: 2,
                        mb: 2,
                        py: 1.5,
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                      startIcon={isLoading && <CircularProgress size={24} color="inherit" />}
                    >
                      {isLoading ? "Creating Account..." : "Register"}
                    </StyledButton>
                    
                    {/* Divider with enhanced styling */}
                    <Divider 
                      sx={{ 
                        my: 2,
                        position: 'relative',
                        '&::before, &::after': {
                          content: '""',
                          position: 'absolute',
                          top: '50%',
                          height: '1px',
                          width: '35%',
                          background: 'linear-gradient(90deg, rgba(0,0,0,0.05), rgba(0,0,0,0.1))'
                        },
                        '&::before': {
                          left: 0
                        },
                        '&::after': {
                          right: 0
                        }
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary', 
                          px: 2,
                          fontSize: '0.875rem',
                          position: 'relative',
                          zIndex: 1,
                          backgroundColor: '#fff',
                          padding: '0 16px'
                        }}
                      >
                        Or
                      </Typography>
                    </Divider>
                    
                    {/* Login Link with enhanced styling */}
                    <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                      >
                        Already have an account?{' '}
                        <Link 
                          to="/login"
                          style={{ 
                            fontWeight: 600,
                            textDecoration: 'none',
                            color: theme.palette.primary.main,
                            position: 'relative',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Sign in
                        </Link>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                {/* Enhanced Enterprise Features Footer */}
                <Box 
                  sx={{ 
                    py: 2,
                    px: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="body2" fontWeight="500" color="text.secondary" sx={{ mb: 1 }}>
                    Your membership includes enterprise-grade features
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {[
                      { icon: <SecurityOutlinedIcon />, text: "Enhanced Security" },
                      { icon: <BusinessCenterOutlinedIcon />, text: "Business Tools" },
                      { icon: <VerifiedUserIcon />, text: "Compliance Ready" }
                    ].map((feature, index) => (
                      <Tooltip key={index} title={feature.text} arrow>
                        <Avatar 
                          sx={{ 
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            width: 36,
                            height: 36,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          {feature.icon}
                        </Avatar>
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              </GradientPaper>
            </Fade>
          </Grid>
          
          {/* Right side - enterprise features - only visible on desktop */}
          {!isTablet && (
            <Grid item md={5}>
              <Box sx={{ color: 'white' }}>
                <Typography 
                  variant="h4" 
                  fontWeight="700" 
                  gutterBottom
                  sx={{ 
                    textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    '&::after': {
                      content: '""',
                      display: 'block',
                      width: 60,
                      height: 4,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      ml: 2,
                      boxShadow: '0 0 10px rgba(25, 118, 210, 0.5)'
                    }
                  }}
                >
                  Enterprise Features
                </Typography>
                
                <Grid container spacing={2}>
                  {enterpriseFeatures.map((feature, index) => (
                    <Grid item xs={12} key={index}>
                      <Fade in={true} timeout={800 + (index * 300)}>
                        <FeatureCard>
                          <CardContent sx={{ p: 3 }}>
                            <Box display="flex" alignItems="center" mb={1}>
                              {feature.icon}
                              <Typography variant="h6" fontWeight="600" ml={2} color="primary">
                                {feature.title}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {feature.text}
                            </Typography>
                          </CardContent>
                        </FeatureCard>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          )}
        </Grid>
        
        {/* Footer Copyright */}
        <Typography 
          variant="caption" 
          color="white" 
          align="center" 
          sx={{ 
            mt: 4, 
            opacity: 0.85, 
            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
            display: 'block'
          }}
        >
          © {new Date().getFullYear()} {tenant?.name || "Enterprise Portal"}. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

export default RegisterPage;
